import type { WishlistsState } from '~/composables/useWishlists/wishlists';
import type { WishlistPropertyResponse } from '~/core/entities/api/ApiWishlist';
import type { PropertyFavorited } from '~/types/mixpanel';
import type { ApplicationError } from '~~/core/helpers/result';

import { useEvents } from '~/composables/events/useEvents';
import { useLoginModal } from '~/composables/useLoginModal';
import { useOfflineFavoriteCountSync } from '~/composables/useOfflineFavoriteCountSync';
import { useOfflineFavorites } from '~/composables/useOfflineFavorites';
import { logger } from '~/core/service/logger';
import { MixpanelProps } from '~/core/service/mixpanel';

export const SHORTS_DEFER_LOGIN_FAVORITE_COUNT = 3;

let isSyncingOfflineFavorites = false;

/**
 * Options for tracking favorite toggle events
 */
export interface UseToggleFavoriteOptions {
	/** Current page path for analytics */
	currentPage?: string;
	/** Whether the property has exclusive tag */
	tagExclusive?: boolean;
	/** Company name for tracking */
	companyName?: string;
	/** Agent name for tracking */
	agentName?: string;
	/** Whether the click originated from a card */
	clickCard?: boolean;
	/** Whether the click originated from property details */
	clickProperty?: boolean;
	/** Shorts feed: asking price of the currently viewed property */
	askingPrice?: number | null;
	/** Shorts feed: region of the currently viewed property */
	regionName?: string;
	/** Shorts feed: commercial_id of the entry-point property */
	inicialCommercialId?: string;
	/** Shorts feed: asking price of the entry-point property */
	inicialAskingPrice?: number | null;
	/** Shorts feed: region of the entry-point property */
	inicialRegionName?: string;
	/** Whether the property has Shorts video available in the database. */
	videoShorts?: boolean;
}

/** Optional hooks for surfaces that open over the page (e.g. shorts video pause). */
export interface UseToggleFavoriteSyncOptions {
	/** Called when the login modal opens/closes after a logged-out favorite tap. */
	onLoginModalOverlayOpenChange?: (isOpen: boolean) => void;
	/**
	 * When set, defers the login modal until the user has favorited this many
	 * properties while logged out. Icon updates immediately on every click.
	 * Only used in the shorts feed.
	 */
	deferLoginUntilCount?: number;
}

/**
 * Return type for toggle favorite operation
 */
interface ToggleResult {
	error: string | null;
}

/**
 * Interface for pending favorite action when user is not logged in
 */
interface PendingFavoriteAction {
	hasPending: boolean;
	targetState: boolean;
}

/**
 * Result tuple from wishlist toggle operations
 */
type WishlistToggleResult = readonly [
	WishlistPropertyResponse | null,
	ApplicationError | null
];

/**
 * Lazy-loaded wishlist operations interface
 */
interface WishlistOperations {
	toggleWishlist: (
		propertyId: string,
		isFavorite: boolean
	) => Promise<WishlistToggleResult>;
	updateLocalWishlist: (propertyId: string, isFavorite: boolean) => void;
}

/**
 * Return type for useToggleFavoriteSync composable
 */
interface UseToggleFavoriteSyncReturn {
	isFavorite: ComputedRef<boolean>;
	isLoading: Ref<boolean>;
	isValidData: ComputedRef<boolean>;
	handleToggleFavorite: () => Promise<ToggleResult>;
}

/**
 * Synchronous version of useToggleFavorite that doesn't block SSR.
 * Uses useState directly instead of awaiting useWishlists().
 * Wishlist operations are lazily loaded on client when needed.
 */
export function useToggleFavoriteSync(
	propertyId: MaybeRefOrGetter<string | null | undefined>,
	commercialId: MaybeRefOrGetter<string | null | undefined>,
	eventData?: MaybeRefOrGetter<UseToggleFavoriteOptions | undefined>,
	sectionName?: MaybeRefOrGetter<string | undefined>,
	_initialFavoriteState?: MaybeRefOrGetter<boolean>,
	options?: UseToggleFavoriteSyncOptions
): UseToggleFavoriteSyncReturn {
	const propertyIdRef = toRef(propertyId);
	const commercialIdRef = toRef(commercialId);
	const eventDataRef = toRef(eventData);
	const sectionNameRef = toRef(sectionName);

	// Use useState directly - no await needed
	const wishlistsState = useState<WishlistsState | null>('wishlists');

	const { loggedIn: isUserLoggedIn } = useUserSession();

	// Lazy-loaded wishlist operations (only on client when needed)
	let wishlistOps: WishlistOperations | null = null;

	const pendingAction = ref<PendingFavoriteAction>({
		hasPending: false,
		targetState: false
	});

	// Tracks the optimistic favorite state for logged-out users
	// This allows proper toggling behavior before login
	const optimisticLoggedOutState = ref<boolean>(false);

	const deferLoginUntilCount = options?.deferLoginUntilCount;
	const offlineFavorites = deferLoginUntilCount ? useOfflineFavorites() : null;
	const favoriteCountSync = deferLoginUntilCount
		? useOfflineFavoriteCountSync()
		: null;

	// Overrides isFavorite while a post-login toggle is in flight.
	// The wishlist plugin refetches wishlists on login and may overwrite the
	// local update before the server has processed our toggle. This ref keeps
	// the UI consistent until the dust settles.
	const pendingFavoriteOverride = ref<boolean | null>(null);

	const isLoading = ref(false);
	let isCleanedUp = false;

	const isValidData = computed(() => {
		const propId = propertyIdRef.value;
		const commId = commercialIdRef.value;
		return Boolean(
			propId &&
			commId &&
			typeof propId === 'string' &&
			typeof commId === 'string'
		);
	});

	const isFavorite = computed(() => {
		const propId = propertyIdRef.value;
		if (!propId || typeof propId !== 'string') {
			return false;
		}

		// Post-login toggle in flight — keep the UI stable until confirmed
		if (pendingFavoriteOverride.value !== null) {
			return pendingFavoriteOverride.value;
		}

		if (!isUserLoggedIn.value && offlineFavorites && propId) {
			return offlineFavorites.isOfflineFavorite(propId);
		}

		if (!isUserLoggedIn.value) {
			return optimisticLoggedOutState.value;
		}

		const favoriteIds = wishlistsState.value?.favoritePropertyIds ?? [];
		if (favoriteIds.length === 0) {
			return false;
		}

		return favoriteIds.includes(propId);
	});

	/**
	 * Lazily load wishlist operations (only on client)
	 */
	async function getWishlistOps(): Promise<WishlistOperations> {
		if (wishlistOps) return wishlistOps;

		if (import.meta.server) {
			throw new Error('Wishlist operations should not be called on server');
		}

		const { useWishlists } =
			await import('~/composables/useWishlists/wishlists');
		const ops = await useWishlists();
		wishlistOps = {
			toggleWishlist: ops.toggleWishlist,
			updateLocalWishlist: ops.updateLocalWishlist
		};
		return wishlistOps;
	}

	/**
	 * Updates local wishlist state for optimistic UI updates
	 */
	function updateLocalWishlistState(propId: string, isFav: boolean): void {
		if (!wishlistsState.value) return;

		const favoriteIds = wishlistsState.value.favoritePropertyIds ?? [];

		if (isFav) {
			if (!favoriteIds.includes(propId)) {
				wishlistsState.value.favoritePropertyIds = [...favoriteIds, propId];
			}
		} else {
			wishlistsState.value.favoritePropertyIds = favoriteIds.filter(
				id => id !== propId
			);
		}
		wishlistsState.value.propertyCount =
			wishlistsState.value.favoritePropertyIds.length;
	}

	// Client-side watcher for login state
	let stopLoginWatcher: (() => void) | null = null;
	let stopModalWatcher: (() => void) | null = null;

	if (import.meta.client) {
		const { trackPropertyFavorited } = useEvents();
		const { openLoginModal, modalState } = useLoginModal();

		if (favoriteCountSync && getCurrentScope()) {
			const unsubscribe = favoriteCountSync.initListener(count => {
				if (count >= (deferLoginUntilCount ?? Infinity)) {
					openLoginModal({ context: 'favorites' });
				}
			});
			onScopeDispose(unsubscribe);
		}

		const loginModalOverlayPauseActive = ref(false);

		function setLoginModalOverlayPause(nextOpen: boolean): void {
			const cb = options?.onLoginModalOverlayOpenChange;
			if (!cb) return;
			if (nextOpen) {
				if (!loginModalOverlayPauseActive.value) {
					loginModalOverlayPauseActive.value = true;
					cb(true);
				}
			} else if (loginModalOverlayPauseActive.value) {
				loginModalOverlayPauseActive.value = false;
				cb(false);
			}
		}

		stopLoginWatcher = watch(
			isUserLoggedIn,
			async (newLoggedIn, oldLoggedIn) => {
				if (isCleanedUp) return;
				if (!newLoggedIn || oldLoggedIn) return;

				setLoginModalOverlayPause(false);

				if (offlineFavorites) {
					await syncOfflineFavorites();
					return;
				}

				if (!pendingAction.value.hasPending) return;

				isLoading.value = true;
				try {
					await executePendingAction();
				} finally {
					isLoading.value = false;
				}
			}
		);

		stopModalWatcher = watch(
			() => modalState.value.isOpen,
			(isOpen, wasOpen) => {
				if (isCleanedUp) return;

				if (wasOpen && !isOpen) {
					setLoginModalOverlayPause(false);
				}

				// In deferred mode the icon state lives in localStorage — no revert needed.
				if (
					!offlineFavorites &&
					wasOpen &&
					!isOpen &&
					pendingAction.value.hasPending
				) {
					setTimeout(() => {
						if (!isUserLoggedIn.value && pendingAction.value.hasPending) {
							const { targetState } = pendingAction.value;
							pendingAction.value.hasPending = false;
							optimisticLoggedOutState.value = !targetState;
						}
					}, 0);
				}
			}
		);

		/**
		 * Tracks favorite action with analytics
		 */
		function trackFavoriteAction(isFav: boolean): void {
			const currentEventData = eventDataRef.value;
			const currentCommercialId = commercialIdRef.value;

			if (
				!currentEventData ||
				!currentCommercialId ||
				typeof currentCommercialId !== 'string'
			)
				return;

			const params: PropertyFavorited = {
				[MixpanelProps.SECTION_NAME]: sectionNameRef.value,
				[MixpanelProps.COMMERCIAL_ID]: currentCommercialId,
				[MixpanelProps.CURRENT_PAGE]: currentEventData.currentPage,
				[MixpanelProps.TAG_EXCLUSIVE]: currentEventData.tagExclusive,
				[MixpanelProps.COMPANY_NAME]: currentEventData.companyName,
				[MixpanelProps.AGENT_NAME]: currentEventData.agentName,
				[MixpanelProps.CLICK_CARD]: currentEventData.clickCard,
				[MixpanelProps.CLICK_PROPERTY]: currentEventData.clickProperty,
				[MixpanelProps.USER_STATUS]: isUserLoggedIn.value
					? 'logged in'
					: 'logged out',
				...(currentEventData.inicialCommercialId !== undefined && {
					[MixpanelProps.ASKING_PRICE]: currentEventData.askingPrice ?? null,
					[MixpanelProps.REGION_NAME]: currentEventData.regionName ?? '',
					[MixpanelProps.INICIAL_COMMERCIAL_ID]:
						currentEventData.inicialCommercialId,
					[MixpanelProps.INICIAL_ASKING_PRICE]:
						currentEventData.inicialAskingPrice ?? null,
					[MixpanelProps.INICIAL_REGION_NAME]:
						currentEventData.inicialRegionName ?? ''
				}),
				...(currentEventData.videoShorts !== undefined && {
					[MixpanelProps.VIDEO_SHORTS]: currentEventData.videoShorts
				})
			};

			if (isFav) {
				params[MixpanelProps.CLICK_FAVORITE] = true;
			} else {
				params[MixpanelProps.CLICK_UNFAVORITE] = true;
			}

			trackPropertyFavorited(params);
		}

		/**
		 * Syncs all offline-favorited properties to the wishlist API after login.
		 * Takes the IDs atomically (clears first) so concurrent instances don't double-sync.
		 */
		async function syncOfflineFavorites(): Promise<void> {
			if (!offlineFavorites || isSyncingOfflineFavorites) return;
			const ids = offlineFavorites.getIds();
			if (ids.length === 0) return;

			isSyncingOfflineFavorites = true;
			offlineFavorites.clearAll();

			try {
				const ops = await getWishlistOps();
				for (const id of ids) {
					const [result, error] = await ops.toggleWishlist(id, false);
					if (error) {
						logger.error(error, `Failed to sync offline favorite: ${id}`);
						offlineFavorites.addOfflineFavorite(id);
					} else if (result) {
						updateLocalWishlistState(id, true);
						ops.updateLocalWishlist(id, true);
					}
				}
			} finally {
				isSyncingOfflineFavorites = false;
				favoriteCountSync?.resetSessionCount();
			}
		}

		/**
		 * Executes pending favorite action and clears pending state
		 */
		async function executePendingAction(): Promise<void> {
			const { targetState } = pendingAction.value;
			pendingAction.value.hasPending = false;
			// Reset optimistic state after login
			optimisticLoggedOutState.value = false;

			// Show the desired state immediately while the API call is in flight.
			// This prevents the UI from flickering when the wishlist plugin's
			// post-login refresh overwrites the local state.
			pendingFavoriteOverride.value = targetState;

			const propId = propertyIdRef.value;
			if (propId) {
				const ops = await getWishlistOps();
				const [result, error] = await ops.toggleWishlist(propId, !targetState);

				if (!error && result) {
					updateLocalWishlistState(propId, targetState);
					ops.updateLocalWishlist(propId, targetState);
				}
			}

			// Clear the override once any in-flight wishlist refresh has settled.
			// The wishlist plugin refetches on login; this gives it time to
			// complete so our local update (applied above) isn't overwritten.
			const stop = watch(
				() => wishlistsState.value?.favoritePropertyIds,
				() => {
					// Re-apply local update after the refresh overwrites
					if (propId) {
						updateLocalWishlistState(propId, targetState);
					}
					pendingFavoriteOverride.value = null;
					stop();
				}
			);
			// Safety: clear override after timeout if no refresh arrives
			const timeout = setTimeout(() => {
				pendingFavoriteOverride.value = null;
				stop();
			}, 5000);

			if (getCurrentScope()) {
				onScopeDispose(() => {
					clearTimeout(timeout);
					stop();
				});
			}
		}

		/**
		 * Executes favorite toggle, update state and track analytics
		 */
		async function executeFavoriteToggle(
			targetState?: boolean
		): Promise<ToggleResult> {
			const currentPropertyId = propertyIdRef.value;
			const previousState = isFavorite.value;
			const newState = targetState ?? !isFavorite.value;

			if (!currentPropertyId || typeof currentPropertyId !== 'string') {
				return { error: 'Invalid property ID' };
			}

			isLoading.value = true;
			// Optimistic update
			updateLocalWishlistState(currentPropertyId, newState);
			trackFavoriteAction(newState);

			try {
				const ops = await getWishlistOps();
				const [result, error] = await ops.toggleWishlist(
					currentPropertyId,
					previousState
				);

				if (error) {
					updateLocalWishlistState(currentPropertyId, previousState);
					return { error: error.message };
				}

				// Handle case where removal was not successful
				if (newState === false && result?.removed === false) {
					updateLocalWishlistState(currentPropertyId, previousState);
				}

				return { error: null };
			} catch (err) {
				logger.error(err, 'Unexpected error in toggleFavorite');
				updateLocalWishlistState(currentPropertyId, previousState);
				return { error: 'Erro inesperado ao alterar favorito' };
			} finally {
				isLoading.value = false;
			}
		}

		/**
		 * Sets pending action for post-login execution
		 */
		function setPendingAction(targetState: boolean): void {
			pendingAction.value = {
				hasPending: true,
				targetState
			};
		}

		function toggleOfflineFavoriteState(propId: string): void {
			if (!offlineFavorites!.isOfflineFavorite(propId)) {
				offlineFavorites!.addOfflineFavorite(propId);
				trackFavoriteAction(true);
				const count = favoriteCountSync!.incrementSessionCount();
				if (count >= deferLoginUntilCount!) {
					setLoginModalOverlayPause(true);
					openLoginModal({ context: 'favorites' });
				}
				return;
			}
			offlineFavorites!.removeOfflineFavorite(propId);
			trackFavoriteAction(false);
			favoriteCountSync!.decrementSessionCount();
		}

		/**
		 * Handles favorite toggle with authentication check (client-only)
		 */
		const handleToggleFavorite = useDebounceFn(
			async (): Promise<ToggleResult> => {
				if (isLoading.value) return { error: null };

				if (isUserLoggedIn.value) return executeFavoriteToggle();

				if (offlineFavorites && deferLoginUntilCount) {
					const propId = propertyIdRef.value;
					if (!propId || typeof propId !== 'string') {
						return { error: 'Invalid property ID' };
					}
					toggleOfflineFavoriteState(propId);
					return { error: null };
				}

				const newState = !isFavorite.value;
				optimisticLoggedOutState.value = newState;
				setPendingAction(newState);
				trackFavoriteAction(newState);
				setLoginModalOverlayPause(true);
				openLoginModal({ context: 'favorites' });
				return { error: null };
			},
			250
		);

		// Cleanup watchers on unmount
		if (getCurrentScope()) {
			onScopeDispose(() => {
				if (isCleanedUp) return;
				isCleanedUp = true;
				setLoginModalOverlayPause(false);
				if (stopLoginWatcher) {
					stopLoginWatcher();
					stopLoginWatcher = null;
				}
				if (stopModalWatcher) {
					stopModalWatcher();
					stopModalWatcher = null;
				}
			});
		}

		return {
			isFavorite,
			isLoading,
			isValidData,
			handleToggleFavorite
		};
	}

	// Server-side: return no-op functions
	return {
		isFavorite,
		isLoading,
		isValidData,
		handleToggleFavorite: async () => ({ error: null })
	};
}
